import { fetchWithApiFallback } from './apiBase';

function getAuthHeaders() {
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function fetchAnalyticsWorkbenchState(moduleKey) {
  const res = await fetchWithApiFallback(`analytics/workbench-state/${encodeURIComponent(moduleKey)}`, {
    headers: getAuthHeaders(),
  });
  const data = await res.json();
  if (!res.ok || !data?.success) return null;
  return data?.data || null;
}

export async function saveAnalyticsWorkbenchState(moduleKey, state) {
  const res = await fetchWithApiFallback(`analytics/workbench-state/${encodeURIComponent(moduleKey)}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(state || {}),
  });
  if (!res.ok) throw new Error('Failed to save analytics workbench state');
}

// ── Sign-in workbench prefetch ──────────────────────────────────────────────
//
// Each analytics module reads its localStorage cache synchronously on mount
// and only then reconciles with the server copy. After sign-in on a fresh
// browser (or when the per-user storage snapshot was too large to keep), the
// cache is empty, so the sidebar renders with defaults and visibly "pops"
// into the restored state once the network fetch lands.
//
// prefetchWorkbenchStates() closes that gap: it runs right after
// authentication (while the user is still on the dashboard shell) and seeds
// every module's localStorage cache from the server. By the time a module
// mounts, its synchronous localStorage read already has the state.
//
// Seeding is skipped for modules whose cache already holds sheets — local
// state can be ahead of the server snapshot (see the stale-remote merge note
// in KPIAnalytics), and each module still runs its own remote reconciliation
// on mount.

// localStorage key per analytics workbench module. Keep in sync with the
// *_STATE_KEY constants in the module files (keys are versioned there).
const ANALYTICS_WORKBENCH_LOCAL_KEYS = {
  business_intelligence: 'bi_workbench_v9',
  kpi_analytics: 'kpi_workbench_v2',
  sentiment_analysis: 'sentiment_workbench_v2',
  intelligence_360: 'intelligence360_workbench_v2',
  revenue_forecast: 'forecast_workbench_v1',
  dynamic_pricing: 'dp_workbench_v1',
};

const SENTINEL_SHEETS_KEY = 'sentinel_sheets';
const SENTINEL_ACTIVE_SHEET_KEY = 'sentinel_active_sheet';
const ACCT_INTEL_LEGACY_KEY = 'account_intel_workbench_v1';
const ACCT_INTEL_DURABLE_PREFIX = 'xph_storage_account_intel_workbench_v2_';

function readLocalJson(key) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeLocalJson(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota or private-mode failure — module falls back to remote hydration.
  }
}

function sheetHasWork(sheet) {
  if (!sheet || typeof sheet !== 'object') return false;
  if (sheet.datasetId || sheet.scorecardId) return true;
  if (Array.isArray(sheet.analyses) && sheet.analyses.length > 0) return true;
  if (Array.isArray(sheet.monitors) && sheet.monitors.length > 0) return true;
  if (sheet.kpiData) return true;
  if (sheet.analysisResult && !sheet.analysisResult._cached) return true;
  if (sheet.forecastData && !sheet.forecastData._cached) return true;
  if (sheet.strategiesResult || sheet.optimizationResult || sheet.dossier) return true;
  return false;
}

export function localWorkbookIsBlank(sheets) {
  const list = Array.isArray(sheets) ? sheets : [];
  if (!list.length) return true;
  return list.every((sheet) => !sheetHasWork(sheet));
}

export function workbookSignature(sheets) {
  return (Array.isArray(sheets) ? sheets : []).map((sheet) => [
    sheet?.id || '',
    sheet?.datasetId || '',
    (Array.isArray(sheet?.analyses) ? sheet.analyses : []).map((item) => item?.id).join(','),
    Array.isArray(sheet?.monitors) ? sheet.monitors.length : 0,
    sheet?.kpiData ? 1 : 0,
    sheet?.analysisResult && !sheet.analysisResult._cached ? 1 : 0,
    sheet?.forecastData && !sheet.forecastData._cached ? 1 : 0,
    sheet?.scorecardId || '',
  ].join('|')).join('~');
}

/**
 * A stale server copy must not put back a sheet or analysis the user
 * already removed on this device. Remote state is used only when the
 * local workbook is still the empty default and the user has not edited
 * it while the fetch was in flight.
 */
/**
 * Keep a conversation already on screen. A locally saved transcript, including
 * one the user cleared, wins over the server copy. The server copy is used
 * only when this browser has never stored the assistant transcript.
 */
export function restoreWorkbenchChat(currentMessages, remoteMessages, options = {}) {
  if (Array.isArray(currentMessages) && currentMessages.length) return currentMessages;
  if (options.localSaved) return Array.isArray(currentMessages) ? currentMessages : [];
  return Array.isArray(remoteMessages) ? remoteMessages : [];
}

export function reconcileWorkbenchSheets(localSheets, remoteSheets, options = {}) {
  const local = Array.isArray(localSheets) ? localSheets : [];
  const remote = Array.isArray(remoteSheets) ? remoteSheets : [];
  if (!remote.length) return local;
  if (options.initialSignature != null && workbookSignature(local) !== options.initialSignature) {
    return local;
  }
  if (localWorkbookIsBlank(local)) return remote;
  return local;
}

function hasUsableSheets(state) {
  return Array.isArray(state?.sheets) && state.sheets.some((s) => (
    !!s?.datasetId
    || !!s?.scorecardId
    || (Array.isArray(s?.analyses) && s.analyses.length > 0)
  ));
}

function scorecardSheetsFromList(rows) {
  return (Array.isArray(rows) ? rows : []).filter((row) => row?.id).map((row) => ({
    id: `sheet-${row.id}`,
    name: row.title || 'Scorecard',
    datasetId: row.datasetId || null,
    scorecardId: row.id,
    template: row.template || 'custom',
  }));
}

async function prefetchScorecards() {
  const existing = readLocalJson('xph_scorecards_sheets');
  const localSheets = Array.isArray(existing?.sheets) ? existing.sheets : [];
  const localBlank = localWorkbookIsBlank(localSheets);
  let remoteSheets = [];
  if (localBlank) {
    const remote = await fetchAnalyticsWorkbenchState('scorecards');
    if (Array.isArray(remote?.sheets)) remoteSheets = remote.sheets;
  }
  let listed = [];
  try {
    const res = await fetchWithApiFallback('scorecards', { headers: getAuthHeaders() });
    if (res.ok) {
      const body = await res.json();
      listed = scorecardSheetsFromList(body?.scorecards);
    }
  } catch { /* the module lists again when it opens */ }
  const base = localBlank && remoteSheets.length ? remoteSheets : localSheets;
  const known = new Set(base.map((sheet) => sheet?.scorecardId).filter(Boolean));
  const missing = listed.filter((sheet) => !known.has(sheet.scorecardId));
  if (!missing.length && !(localBlank && remoteSheets.length)) return;
  const sheets = [
    ...missing,
    ...base.filter((sheet) => sheet?.scorecardId || sheet?.datasetId),
  ];
  if (!sheets.length) return;
  const activeSheetId = sheets.some((sheet) => sheet.id === existing?.activeSheetId)
    ? existing.activeSheetId
    : sheets[0].id;
  writeLocalJson('xph_scorecards_sheets', { ...(existing || {}), sheets, activeSheetId });
}

async function prefetchAnalyticsModule(moduleKey, localKey) {
  const existing = readLocalJson(localKey);
  if (hasUsableSheets(existing)) return;
  const remote = await fetchAnalyticsWorkbenchState(moduleKey);
  if (!remote || !Array.isArray(remote.sheets) || remote.sheets.length === 0) return;
  writeLocalJson(localKey, { ...(existing || {}), ...remote });
}

async function prefetchSentinelState() {
  const existing = readLocalJson(SENTINEL_SHEETS_KEY);
  if (Array.isArray(existing) && existing.some((s) => !!s?.datasetId)) return;
  const res = await fetchWithApiFallback('sentinel/workbench-state', { headers: getAuthHeaders() });
  if (!res.ok) return;
  const data = await res.json();
  const state = data?.state;
  if (!state || !Array.isArray(state.sheets) || state.sheets.length === 0) return;
  // Same normalization SentinelIntel applies on remote hydration; monitors
  // are re-fetched from the server on mount.
  const normalizedSheets = state.sheets.slice(0, 100).map((sheet, index) => ({
    id: String(sheet?.id || `sheet-${index + 1}`),
    name: String(sheet?.name || `Sheet ${index + 1}`),
    datasetId: sheet?.datasetId || null,
    datasetName: sheet?.datasetName || null,
    sheetName: sheet?.sheetName || null,
    monitors: [],
  }));
  writeLocalJson(SENTINEL_SHEETS_KEY, normalizedSheets);
  const activeId = String(state.activeSheetId || '');
  if (activeId && normalizedSheets.some((s) => s.id === activeId)) {
    try { window.localStorage.setItem(SENTINEL_ACTIVE_SHEET_KEY, activeId); } catch { /* ignore */ }
  }
}

async function prefetchAccountIntelState(userId) {
  const durableKey = `${ACCT_INTEL_DURABLE_PREFIX}${userId || 'anonymous'}`;
  if (hasUsableSheets(readLocalJson(durableKey)) || hasUsableSheets(readLocalJson(ACCT_INTEL_LEGACY_KEY))) return;
  const res = await fetchWithApiFallback('/customer-dossier/workbench-state', { headers: getAuthHeaders() });
  if (!res.ok) return;
  const data = await res.json();
  const state = data?.data;
  if (!data?.success || !state || !Array.isArray(state.sheets) || state.sheets.length === 0) return;
  const payload = {
    sheets: state.sheets,
    activeSheetId: state.sheets.some((s) => s?.id === state.activeSheetId) ? state.activeSheetId : state.sheets[0]?.id,
  };
  writeLocalJson(durableKey, payload);
  writeLocalJson(ACCT_INTEL_LEGACY_KEY, payload);
}

export async function prefetchWorkbenchStates(userId) {
  if (typeof window === 'undefined') return;
  const uid = userId == null ? '' : String(userId);
  if (!uid) return;

  const jobs = [
    ...Object.entries(ANALYTICS_WORKBENCH_LOCAL_KEYS).map(
      ([moduleKey, localKey]) => prefetchAnalyticsModule(moduleKey, localKey),
    ),
    prefetchScorecards(),
    prefetchSentinelState(),
    prefetchAccountIntelState(uid),
  ];
  // Individual failures (403 on plan-gated modules, network blips) are fine —
  // the module's own remote hydration remains the fallback.
  await Promise.allSettled(jobs);
}
